import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Button, message, Typography } from 'antd';
import ResourceUploader from '../../../../components/Business/ResourceUploader';
import { orderAuditApi } from '../../../../services/process/order-audit';
import { OperationAction } from '../../../../types/process/operation-log';
import { MergedOrder } from '../../../../types/process/order';

const { Text } = Typography;

export interface PrepayRentModalProps {
  visible: boolean;
  order: MergedOrder | null;
  mode?: 'view' | 'edit';
  onCancel: () => void;
  onSuccess?: () => void;
}

const PrepayRentModal: React.FC<PrepayRentModalProps> = ({
  visible,
  order,
  mode = 'edit',
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && order) {
      form.resetFields();
      form.setFieldsValue({
        actualAdvancePeriod: order.audit?.actualAdvancePeriod,
        prepayVoucher: order.audit?.prepayVoucher || [],
      });
    }
  }, [visible, order, form]);

  const handleSubmit = async () => {
    if (!order) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload = {
        audit: {
          prepayVoucher: values.prepayVoucher,
          actualAdvancePeriod: values.actualAdvancePeriod,
        },
      };

      await orderAuditApi.changeStatus(order.order.orderNo!, OperationAction.SubmitPrepay, payload);
      message.success('预收凭证提交成功');
      onSuccess?.();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  const isView = mode === 'view';

  return (
    <Modal
      title={isView ? "查看预收凭证" : "订单预收"}
      open={visible}
      onCancel={onCancel}
      destroyOnClose
      footer={
        isView ? null : [
          <Button key="cancel" onClick={onCancel}>
            取消
          </Button>,
          <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
            提交预收
          </Button>,
        ]
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">订单号: {order.order.orderNo}</Text>
      </div>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="应收期数">
          <Text strong style={{ fontSize: 16 }}>{order.audit?.advancePeriod || 0}</Text>
        </Form.Item>

        <Form.Item
          name="actualAdvancePeriod"
          label="实际预收期数"
          rules={isView ? [] : [{ required: true, message: '请输入实际预收期数' }]}
        >
          {isView ? (
            <Text>{order.audit?.actualAdvancePeriod || '-'}</Text>
          ) : (
            <InputNumber style={{ width: '100%' }} placeholder="请输入实际预收期数" min={1} precision={0} />
          )}
        </Form.Item>

        <Form.Item
          name="prepayVoucher"
          label="付款凭证"
          rules={isView ? [] : [{ required: true, message: '请上传付款凭证' }]}
        >
          <ResourceUploader
            maxCount={5}
            disabled={isView}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PrepayRentModal;
